import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import DatePicker from '../../components/DatePicker';

const DateCalculator = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [excWeekends, setExcWeekends] = useState(false);

  const diff = useMemo(() => {
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s) || isNaN(e)) return null;
    const msPerDay = 1000 * 60 * 60 * 24;
    const direction = s <= e ? 1 : -1;
    const [a, b] = s <= e ? [s, e] : [e, s];
    const rawDays = Math.round((b - a) / msPerDay);
    // weekdays only
    let weekdays = 0;
    if (excWeekends) {
      const cur = new Date(a);
      for (let i = 0; i <= rawDays; i++) {
        const d = cur.getDay();
        if (d !== 0 && d !== 6) weekdays++;
        cur.setDate(cur.getDate() + 1);
      }
    }
    let years = b.getFullYear() - a.getFullYear();
    let months = b.getMonth() - a.getMonth();
    let days = b.getDate() - a.getDate();
    if (days < 0) { months--; days += new Date(b.getFullYear(), b.getMonth(), 0).getDate(); }
    if (months < 0) { years--; months += 12; }
    return { rawDays, weekdays, years, months, days, direction, weeks: rawDays / 7 };
  }, [start, end, excWeekends]);

  // Add/subtract days tab
  const [baseDate, setBaseDate] = useState(today);
  const [delta, setDelta] = useState(30);
  const [unit, setUnit] = useState('days');
  const [op, setOp] = useState('add');

  const result2 = useMemo(() => {
    const d = new Date(baseDate);
    if (isNaN(d)) return null;
    const m = op === 'add' ? 1 : -1;
    if (unit === 'days') d.setDate(d.getDate() + m * delta);
    if (unit === 'weeks') d.setDate(d.getDate() + m * delta * 7);
    if (unit === 'months') d.setMonth(d.getMonth() + m * delta);
    if (unit === 'years') d.setFullYear(d.getFullYear() + m * delta);
    return d;
  }, [baseDate, delta, unit, op]);

  return (
    <CalculatorLayout
      title="Date Calculator"
      subtitle="Calculate days between dates, or add/subtract days from a date."
      category="Other"
      description={<p>Find the duration between two dates in years, months, days, weeks — optionally excluding weekends. You can also add or subtract days, weeks, months, or years from any date.</p>}
    >
      <Tabs defaultValue="between">
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="between">Days Between</TabsTrigger>
          <TabsTrigger value="addsub">Add / Subtract</TabsTrigger>
        </TabsList>

        <TabsContent value="between" className="mt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label className="text-sm mb-1.5 block">Start Date</Label>
                <DatePicker value={start} onChange={setStart} />
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">End Date</Label>
                <DatePicker value={end} onChange={setEnd} />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={excWeekends} onChange={(e) => setExcWeekends(e.target.checked)} className="accent-saffron-500 w-4 h-4" />
                Count only weekdays (exclude Sat & Sun)
              </label>
            </div>

            <div className="bg-gradient-to-br from-indgreen-50 to-white rounded-xl border border-slate-200 p-5">
              {diff ? (
                <>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Duration</div>
                  <div className="mt-2 flex items-baseline gap-1 flex-wrap">
                    <span className="font-display text-4xl font-bold text-bharat-navy display-font">{diff.years}</span>
                    <span className="text-xs text-slate-500 mr-2">yr</span>
                    <span className="font-display text-3xl font-bold text-saffron-600 display-font">{diff.months}</span>
                    <span className="text-xs text-slate-500 mr-2">mo</span>
                    <span className="font-display text-3xl font-bold text-indgreen-600 display-font">{diff.days}</span>
                    <span className="text-xs text-slate-500">days</span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                    <Res label="Total Days" v={diff.rawDays.toLocaleString()} />
                    <Res label="Total Weeks" v={diff.weeks.toFixed(2)} />
                    {excWeekends && <Res label="Weekdays" v={diff.weekdays.toLocaleString()} tone="green" />}
                    <Res label="Direction" v={diff.direction === 1 ? 'Forward' : 'Backward'} />
                  </div>
                </>
              ) : <p className="text-sm text-slate-500">Please enter valid dates.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="addsub" className="mt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label className="text-sm mb-1.5 block">Starting Date</Label>
                <DatePicker value={baseDate} onChange={setBaseDate} />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setOp('add')} variant={op === 'add' ? 'default' : 'outline'} className={op === 'add' ? 'bg-indgreen-500 hover:bg-indgreen-600 text-white' : ''}>+ Add</Button>
                <Button onClick={() => setOp('sub')} variant={op === 'sub' ? 'default' : 'outline'} className={op === 'sub' ? 'bg-saffron-500 hover:bg-saffron-600 text-white' : ''}>− Subtract</Button>
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Amount</Label>
                <Input type="number" value={delta} onChange={(e) => setDelta(+e.target.value)} />
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Unit</Label>
                <div className="flex gap-2 flex-wrap">
                  {['days', 'weeks', 'months', 'years'].map(u => (
                    <Button key={u} size="sm" variant={unit === u ? 'default' : 'outline'} onClick={() => setUnit(u)} className={unit === u ? 'bg-bharat-navy text-white hover:bg-slate-800' : ''}>{u}</Button>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-saffron-50 to-white rounded-xl border border-slate-200 p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Result Date</div>
              <div className="font-display text-3xl font-bold text-bharat-navy display-font mt-2">
                {result2 ? result2.toDateString() : '—'}
              </div>
              {result2 && (
                <div className="mt-2 text-sm text-slate-600">
                  {result2.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </CalculatorLayout>
  );
};

const Res = ({ label, v, tone }) => (
  <div className={`p-2.5 rounded-md border ${tone === 'green' ? 'bg-indgreen-50 border-indgreen-200' : 'bg-white border-slate-100'}`}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`display-font font-semibold mt-0.5 ${tone === 'green' ? 'text-indgreen-700' : 'text-bharat-navy'}`}>{v}</div>
  </div>
);

export default DateCalculator;
